import { Component } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subject, debounceTime, distinctUntilChanged } from "rxjs";
import { CdiListingDto } from "../../../core/models/cdi/CdiListingDto";
import { DateFilter } from "../../../core/models/common/date-filter-request.dto";
import { CDISummaryService } from "../../../core/services/cdi-resolution-summary.service";
import { LoaderService } from "../../../core/services/loader.service";
import {
  SharedService,
  exportExcel,
} from "../../../core/services/shared.service";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { CommonModule } from "@angular/common";
import { DateFilterComponent } from "../../../shared/components/date-filter/date-filter.component";
import { ModalStackerComponent } from "../../../shared/components/modal-stacker/modal-stacker.component";
import { HidedynamicsdatePipe } from "../../../shared/pipes/hidedate/hidedynamicsdate.pipe";
import { CdiResolutionSummaryComponent } from "../cdi-resolution-summary/cdi-resolution-summary.component";
import { FilterMetadata } from "primeng/api";
import { GenericFilterRequestDto } from "../../../core/models/generic-filter-request.dto";
import { GenericSortRequestDto } from "../../../core/models/generic-sort-request.dto";
import { GenericRequestDto } from "../../../core/models/generic-request.dto";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";

@UntilDestroy()
@Component({
  selector: "jni-cdi-listing",
  templateUrl: "./cdi-listing.component.html",
  styleUrls: ["./cdi-listing.component.scss"],
  imports: [
    TableModule,
    CommonModule,
    DateFilterComponent,
    ModalStackerComponent,
    HidedynamicsdatePipe,
    CdiResolutionSummaryComponent,
    ButtonModule,
    CardModule,
    InputTextModule,
  ],
})
export class CdiListingComponent {
  cdiList: CdiListingDto[] = [];
  cdiSummaryDisplayModal = false;
  CDIResolutionNumber = "";
  saleOrderId = "";
  poNumber = "";
  recId = 0;
  totalRecords = 0;
  defaultTake = 50;
  lazyLoadEvent!: TableLazyLoadEvent;
  filterTextChanged: Subject<Event | CdiListingDto[] | DateFilter> =
    new Subject<Event | CdiListingDto[] | DateFilter>();
  filterDebounceTime = 1000;
  descriptionCharacterCond = false;

  invoiceDateFilter = {} as DateFilter;
  assignedDateFilter = {} as DateFilter;
  invoiceDateFieldName = "invoiceDate";
  assignedDateFieldName = "assigned";
  filterMode = "server";

  constructor(
    private _cdiSummaryService: CDISummaryService,
    private _loaderService: LoaderService,
    private _sharedService: SharedService
  ) {
    this.clearAndSetSimpleOrderSearchResponseObj();
    this._sharedService.setOrderSearchValue("");
    this._sharedService.setOrderSearchPlaceholderValue("Search");
  }

  clearAndSetSimpleOrderSearchResponseObj() {
    const orderSearch: string[] = [];
    this._sharedService.setSimpleOrderSearchResponseObject(orderSearch);
  }

  exportExcel() {
    exportExcel(this.cdiList, "CDI");
  }

  getCDIListing(event: TableLazyLoadEvent) {
    this.lazyLoadEvent = event;
    const eventFilterArray = Object.keys(event.filters ?? {});
    const filter: GenericFilterRequestDto[] = eventFilterArray.map(x => {
      const tableFilter = event?.filters?.[x] as FilterMetadata;
      const data = {
        columnName: x ?? "",
        matchMode: tableFilter.matchMode ?? "",
        tableName: "",
        value: tableFilter.value ?? "",
      } as GenericFilterRequestDto;
      return data;
    });

    const sortArray: GenericSortRequestDto[] =
      event.multiSortMeta?.map(x => {
        return {
          ...x,
        } as GenericSortRequestDto;
      }) ?? [];

    filter.forEach(x => {
      if (x.columnName == "invoicePreviewNumber") {
        x.columnName = "invoicePreviewNumber";
        x.tableName = "invoicePreviewNumber";
      }
      if (x.columnName == "customerNumber") {
        x.columnName = "customerNumber";
        x.tableName = "customerNumber";
      }
      if (x.columnName == "orderNumber") {
        x.columnName = "orderNumber";
        x.tableName = "orderNumber";
      }

      if (x.columnName == "vendorName") {
        x.columnName = "VendorNameD365";
        x.tableName = "OcrInvoiceMatching";
      }

      if (x.columnName == "purchaseOrder") {
        x.columnName = "PurchaseOrderD365";
        x.tableName = "OcrInvoiceMatching";
      }

      if (x.columnName == "salesOrder") {
        x.columnName = "SalesOrderD365";
        x.tableName = "OcrInvoiceMatching";
      }

      if (x.columnName == "customer") {
        x.columnName = "CustomerAccountD365";
        x.tableName = "OcrInvoiceMatching";
      }

      if (x.columnName == "customerName") {
        x.columnName = "CustomerNameD365";
        x.tableName = "OcrInvoiceMatching";
      }
      if (x.columnName == "orderBacName") {
        x.tableName = "employee";
      }

      if (x.columnName == "bcName") {
        x.tableName = "OcrInvoiceMatching";
        x.columnName = "BcNameD365";
      }

      const invoiceDate =
        this._sharedService.convertDateFormat(
          this.invoiceDateFilter.startDate ?? ""
        ) +
        "|" +
        this._sharedService.convertDateFormat(
          this.invoiceDateFilter.endDate ?? ""
        );

      if (x.columnName == "invoiceDate") {
        x.columnName = "InvoiceDateD365";
        x.tableName = "OcrInvoiceMatching";
        x.matchMode = this.invoiceDateFilter.selectedMatchMode;
        x.value = invoiceDate;
      }

      const assignedDate =
        this._sharedService.convertDateFormat(
          this.assignedDateFilter.startDate ?? ""
        ) +
        "|" +
        this._sharedService.convertDateFormat(
          this.assignedDateFilter.endDate ?? ""
        );
      if (x.columnName == "assigned") {
        x.columnName = "AssignedD365";
        x.tableName = "OcrInvoiceMatching";
        x.matchMode = this.assignedDateFilter.selectedMatchMode;
        x.value = assignedDate;
      }
    });

    this._loaderService.setLoaderState(true);
    const request: GenericRequestDto = {
      skip: event.first ?? 0,
      take: event.rows ?? this.defaultTake,
      filters: filter,
      sort: sortArray,
    };
    this._cdiSummaryService
      .getCDIListing(request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res.data) {
            this.cdiList = res.data;
            this.totalRecords = res.totalRecords ?? 0;
          }
        },
        error: () => {
          this._loaderService.setLoaderState(false);
        },
      });
    // this.cdiList = [{
    //   cdiResolutionNumber: "88965412",
    //   po: "20240081",
    //   vendorName: "The Chest Inc.",
    //   salesOrder: "40103519",
    //   customerNumber: "80019638",
    //   customerName: "Hitachi Solutions inc",
    //   number: "136502",
    //   invoiceDate: "10/4/2022",
    //   invoiceTotal: 123.69,
    //   subTotal: 150.20,
    //   freight: 0.00,
    //   tax: 0.00,
    //   JNIPO: 6696.00,
    //   Unmatched: 0,
    //   assigned: "10/4/2022 23:11",
    //   bcName: "Chris Dyer",
    //   reason: "Extra Items",
    //   note: "Order on hold"
    // }]
  }

  openCDI(
    CDIResolutionNumber: string,
    poNumber: string,
    salesOrder: string,
    recId: number
  ) {
    this.CDIResolutionNumber = CDIResolutionNumber;
    this.poNumber = poNumber;
    this.saleOrderId = salesOrder;
    this.recId = recId;
    this.cdiSummaryDisplayModal = true;
  }

  closeModalForCDI() {
    this.cdiSummaryDisplayModal = false;
  }

  onFilterInput($event: Event | CdiListingDto[] | DateFilter, value = "") {
    if (value === "clear") {
      this.clearFilter($event);
    } else {
      this.applyDateFilter($event);
    }

    if (this.filterTextChanged.observers.length === 0) {
      this.filterTextChanged
        .pipe(debounceTime(this.filterDebounceTime), distinctUntilChanged())
        .subscribe(() => {
          this.getCDIListing(this.lazyLoadEvent);
        });
    }
    this.filterTextChanged.next($event);
  }

  descriptionCharacterCount(obj: string) {
    if (obj?.length > 45) this.descriptionCharacterCond = true;
  }

  clearFilter(event: Event | CdiListingDto[] | DateFilter) {
    if (
      "columnName" in event &&
      event.columnName === this.invoiceDateFieldName
    ) {
      this.invoiceDateFilter = {} as DateFilter;
    } else if (
      "columnName" in event &&
      event.columnName === this.assignedDateFieldName
    ) {
      this.assignedDateFilter = {} as DateFilter;
    }
  }

  applyDateFilter(event: Event | CdiListingDto[] | DateFilter) {
    if (!("startDate" in event) || !("columnName" in event)) {
      return;
    }

    if (event.startDate && event.columnName === this.invoiceDateFieldName) {
      this.invoiceDateFilter = event;
    } else if (
      event.startDate &&
      event.columnName === this.assignedDateFieldName
    ) {
      this.assignedDateFilter = event;
    }
  }
}
